/**
 * Module: Frontend Firebase Services
 * Purpose: Einheitliche Initialisierung von Firebase App/Auth/Firestore/Functions über zentrale Config (hotel-support-chat)
 * Inputs: src/config/firebase.ts (statische Projekt‑Config)
 * Outputs: Exporte (auth, db, functions, collections, helpers)
 * Public Interfaces: Import in Frontend‑Services/Seiten
 * Requirements: [IR-FB-001]
 * Owner: web-frontend
 * Last-Updated: 2025-08-11
 * Tests: Manuell – Starten der App, Lesen von `bookings` im Dashboard/Calendar
 * Risks/Assumptions: Keine ENV‑Platzhalter mehr; nutzt zentrale Config
 */
import { initializeApp, getApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  startAfter,
  serverTimestamp,
  Timestamp,
  DocumentData,
  QueryConstraint,
  documentId,
  CollectionReference,
  DocumentReference
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { app, auth, getTenantDb, getPlatformDb } from '../config/firebase';
export { app, auth, getTenantDb, getPlatformDb };
export const functions = getFunctions(app);

// Auth functions
export const signIn = (email: string, password: string) => 
  signInWithEmailAndPassword(auth, email, password);

export const signOut = () => firebaseSignOut(auth);

// Firestore collections
export const collections = {
  get bookings() { return collection(getTenantDb(), 'bookings'); },
  get bookingChanges() { return collection(getTenantDb(), 'booking_changes'); },
  get specialRequests() { return collection(getTenantDb(), 'special_requests'); },
  get outgoingMessages() { return collection(getTenantDb(), 'outgoing_messages'); },
  get cleaningPreviews() { return collection(getTenantDb(), 'cleaning_previews'); },
  get auditLogs() { return collection(getTenantDb(), 'audit_logs'); },
  get userSettings() { return collection(getTenantDb(), 'user_settings'); },
  get rules() { return collection(getTenantDb(), 'rules'); },
  get alerts() { return collection(getTenantDb(), 'alerts'); },
  // Canonical collections used across services
  get unifiedEmails() { return collection(getTenantDb(), 'unifiedEmails'); },
  get webhookEvents() { return collection(getTenantDb(), 'webhook_events'); },
  get bookingReferences() { return collection(getTenantDb(), 'booking_references'); }
};

// Typed Firestore helpers
export function createTypedCollection<T extends DocumentData>(
  collectionName: string
): CollectionReference<T> {
  return collection(getTenantDb(), collectionName) as CollectionReference<T>;
}

export function createTypedDoc<T extends DocumentData>(
  collectionName: string,
  docId: string
): DocumentReference<T> {
  return doc(getTenantDb(), collectionName, docId) as DocumentReference<T>;
}

// Export commonly used Firestore functions
export {
  doc,
  collection,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  documentId,
  type User,
  type DocumentData,
  type QueryConstraint,
  onAuthStateChanged,
  httpsCallable
};